import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import toast from 'react-hot-toast';
import { GuardData, updateGuard } from '../../manage_guard/service/guardService';
import { FiDollarSign, FiClock } from 'react-icons/fi';

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
  padding: ${theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.secondary};
`;

const GuardInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.light.background.secondary};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing[2]};
`;

const GuardImage = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
`;

const InputIconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  
  svg {
    position: absolute;
    left: ${theme.spacing[3]};
    color: ${theme.colors.light.text.muted};
    z-index: 1;
  }

  input {
    padding-left: ${theme.spacing[10]};
  }
`;

interface GuardSalaryDeploymentModalProps {
    guard: GuardData | null;
    isOpen: boolean;
    onClose: () => void;
    onUpdate: () => void;
    companyId: string;
}

const GuardSalaryDeploymentModal: React.FC<GuardSalaryDeploymentModalProps> = ({
    guard,
    isOpen,
    onClose,
    onUpdate,
    companyId,
}) => {
    const [salary, setSalary] = useState<string>('');
    const [deploymentDate, setDeploymentDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isOpen && guard) {
            setSalary(guard.salary?.toString() || '');

            // Handle deployment date
            if (guard.currentDeployment?.postDate) {
                const postDate = guard.currentDeployment.postDate as any;
                let date: Date;
                if (postDate.seconds) {
                    date = new Date(postDate.seconds * 1000);
                } else {
                    date = new Date(postDate);
                }
                setDeploymentDate(date.toISOString().split('T')[0]);
            } else {
                setDeploymentDate('');
            }

            // Handle end date if guard is already marked for termination but still in currentDeployment
            if (guard.currentDeployment?.endDate) {
                const endDt = guard.currentDeployment.endDate as any;
                let date: Date;
                if (endDt.seconds) {
                    date = new Date(endDt.seconds * 1000);
                } else {
                    date = new Date(endDt);
                }
                setEndDate(date.toISOString().split('T')[0]);
            } else {
                setEndDate('');
            }
        }
    }, [isOpen, guard]);

    const handleSave = async () => {
        if (!guard) return;

        setSaving(true);
        try {
            const newSalary = parseFloat(salary) || 0;
            const updates: Partial<GuardData> = {
                salary: newSalary,
            };

            if (guard.currentDeployment) {
                updates.currentDeployment = {
                    ...guard.currentDeployment,
                    salary: newSalary, // Sync salary to the deployment snapshot
                };

                if (deploymentDate) {
                    updates.currentDeployment.postDate = new Date(deploymentDate).toISOString();
                }
            }

            await updateGuard(guard.id, updates, companyId);

            // Sync salary back to client if guard is deployed
            const clientPath = guard.currentDeployment?.client?.path || guard.currentDeployment?.client?.clientPath;
            if (clientPath && updates.salary !== undefined) {
                try {
                    const { doc, updateDoc } = await import('firebase/firestore');
                    const { db } = await import('../../../firebase/config');
                    const clientRef = doc(db, clientPath);
                    await updateDoc(clientRef, { salary: updates.salary });
                    console.log(`Synced salary ${updates.salary} to client at ${clientPath}`);
                } catch (syncError) {
                    console.error('Error syncing salary to client:', syncError);
                    // We don't want to fail the whole save if client sync fails, but maybe log it
                }
            }

            // Handle termination if end date is set
            if (endDate) {
                const clientPath = guard.currentDeployment?.client?.path || guard.currentDeployment?.client?.clientPath;
                if (clientPath) {
                    // The original comment was: const { terminateDeployment } = await import('../../attendance/service/paymentService'); // Wait, it's in deployment service
                    // Actually I added it to deploymentService.ts
                    const { terminateDeployment } = await import('../../deployment/service/deploymentService');
                    await terminateDeployment(guard.id, clientPath, new Date(endDate), companyId);
                }
            }

            toast.success('Guard data updated successfully');
            onUpdate();
            onClose();
        } catch (error: any) {
            console.error('Error updating guard:', error);
            toast.error('Failed to update guard: ' + (error.message || 'Unknown error'));
        } finally {
            setSaving(false);
        }
    };

    if (!guard) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Set Salary & Deployment Date"
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </>
            }
        >
            <Form>
                <GuardInfo>
                    {guard.image ? (
                        <GuardImage src={guard.image} alt={guard.name} />
                    ) : (
                        <div
                            style={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                backgroundColor: theme.colors.light.background.tertiary,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: theme.typography.fontSize.lg,
                                color: theme.colors.light.text.muted,
                            }}
                        >
                            {guard.name?.charAt(0) || '?'}
                        </div>
                    )}
                    <div>
                        <div style={{ fontWeight: theme.typography.fontWeight.bold }}>{guard.name}</div>
                        <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.light.text.secondary }}>
                            {guard.guardId}
                        </div>
                    </div>
                </GuardInfo>

                <FormGroup>
                    <Label>Monthly Salary (Rs.)</Label>
                    <InputIconWrapper>
                        <FiDollarSign />
                        <Input
                            type="number"
                            value={salary}
                            onChange={(e) => setSalary(e.target.value)}
                            placeholder="Enter monthly salary"
                            dark
                            fullWidth
                        />
                    </InputIconWrapper>
                </FormGroup>

                <FormGroup>
                    <Label>Deployment Date</Label>
                    <InputIconWrapper>
                        <FiClock />
                        <Input
                            type="date"
                            value={deploymentDate}
                            onChange={(e) => setDeploymentDate(e.target.value)}
                            dark
                            fullWidth
                        />
                    </InputIconWrapper>
                    <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.light.text.secondary, fontStyle: 'italic' }}>
                        Note: Recalculates attendance based on this date.
                    </div>
                </FormGroup>

                <FormGroup>
                    <Label>End Deployment Date (Optional)</Label>
                    <InputIconWrapper>
                        <FiClock />
                        <Input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            dark
                            fullWidth
                        />
                    </InputIconWrapper>
                    <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.light.text.secondary, fontStyle: 'italic' }}>
                        Note: Stops attendance and removes guard from deployment after this date.
                    </div>
                </FormGroup>
            </Form>
        </Modal>
    );
};

export default GuardSalaryDeploymentModal;
